import cloupUploadIcon from "@/assets/cloud-upload.svg";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { FileText, Trash, X } from "lucide-react";
import { dilemmasRepo } from "@/lib/repositories";
import { catchError } from "@/lib/errors";
import { handleApiError } from "@/utils/errors";
import { toast } from "sonner";
import type { ApiUploadedFile } from "../../types";
import { formatFileSize } from "@/lib/utils";

const MAX_SIZE = 25 * 1024 * 1024; // 25 MB
export const MAX_FILES = 5;

type UploadingFile = {
  id: string;
  file: File;
  progress: number;
};

interface UploaderProps {
  dilemmaId: string;
  uploadedFiles: ApiUploadedFile[];
  onUploadComplete: (file: ApiUploadedFile) => void;
  onRemove: (fileId: string) => void;
  isEditMode?: boolean;
  onDelete?: (fileId: string) => void;
  isDeleting?: boolean;
  maxFiles?: number;
}

const Uploader = ({
  dilemmaId,
  uploadedFiles,
  onUploadComplete,
  onRemove,
  isEditMode,
  onDelete,
  isDeleting,
  maxFiles = MAX_FILES,
}: UploaderProps) => {
  const { t } = useTranslation();
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);

  const remainingSlots =
    maxFiles - uploadedFiles.length - uploadingFiles.length;
  const isAtLimit = remainingSlots <= 0;

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const filesToUpload = acceptedFiles.slice(0, remainingSlots);
      if (filesToUpload.length === 0) return;

      for (const file of filesToUpload) {
        if (file.size > MAX_SIZE) {
          toast.error(t("modules.dilemmas.form.upload_file_too_large"));
          continue;
        }

        const tempId = crypto.randomUUID();

        setUploadingFiles((prev) => [
          ...prev,
          { id: tempId, file, progress: 0 },
        ]);

        const [error, result] = await catchError(
          dilemmasRepo.uploadFile(
            file,
            dilemmaId,
            ["documentation"],
            (progress) =>
              setUploadingFiles((prev) =>
                prev.map((f) => (f.id === tempId ? { ...f, progress } : f)),
              ),
          ),
        );

        setUploadingFiles((prev) => prev.filter((f) => f.id !== tempId));

        if (error) {
          toast.error(handleApiError(error).message);
          continue;
        }

        onUploadComplete(result);
      }
    },
    [dilemmaId, onUploadComplete, remainingSlots, t],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    maxSize: MAX_SIZE,
    accept: {
      "application/pdf": [".pdf"],
    },
    disabled: isAtLimit,
  });

  return (
    <div className="flex flex-col gap-3">
      <label className="text-sm font-medium">
        {t("modules.dilemmas.form.associated_documentation")}
      </label>

      {!isAtLimit && (
        <div
          {...getRootProps()}
          className={`cursor-pointer p-3 py-6 border border-dashed rounded-md flex flex-col gap-2 items-center justify-center transition-colors ${
            isDragActive ? "border-primary bg-primary/5" : "border-gray-300"
          }`}
        >
          <input {...getInputProps()} />
          <img src={cloupUploadIcon} alt="" className="size-6" />
          <p className="text-gray-500 text-sm text-center">
            <span className="text-indigo-600">
              {t("modules.dilemmas.form.upload_click")}
            </span>{" "}
            {t("modules.dilemmas.form.upload_drag")}
            <br />
            <span className="text-xs">
              ({t("modules.dilemmas.form.upload_max_size")})
            </span>
          </p>
        </div>
      )}

      {uploadingFiles.map((uploading) => (
        <div
          key={uploading.id}
          className="flex items-center gap-3 p-3 border rounded-md"
        >
          <div className="flex-1 min-w-0">
            <div className="flex-1 min-w-0 flex gap-4">
              <FileText className="size-6 text-gray-400" strokeWidth={1.5} />
              <div className="flex flex-col">
                <p className="text-sm font-medium truncate max-w-20">
                  {uploading.file.name}
                </p>
                <p className="text-sm text-gray-400">
                  {formatFileSize(uploading.file.size)}
                </p>
              </div>
            </div>
            <div className="mt-2 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-500 rounded-full transition-all duration-300"
                style={{ width: `${uploading.progress}%` }}
              />
            </div>
          </div>
          <span className="text-sm text-gray-500 shrink-0">
            {uploading.progress}%
          </span>
        </div>
      ))}

      {uploadedFiles.map((file) => (
        <div
          key={file.id}
          className="flex items-center gap-3 p-3 border rounded-md"
        >
          <div className="flex flex-1 gap-4 min-w-0">
            <FileText
              className="size-6 shrink-0 text-gray-400"
              strokeWidth={1.5}
            />
            <div className="flex flex-col mt-0.5 truncate w-full">
              <p className="text-sm font-medium truncate w-full">
                {file.uploaded_file.split("/").pop()}
              </p>
              <p className="text-sm text-gray-400 w-full">
                {formatFileSize(file.file_size)}
              </p>
            </div>
          </div>
          <div>
            {isEditMode && onDelete ? (
              <button
                type="button"
                disabled={isDeleting}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(file.id);
                }}
                className="shrink-0 p-2 rounded-md hover:bg-red-50 text-red-500 hover:text-red-700 disabled:opacity-50"
              >
                <Trash className="size-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(file.id);
                }}
                className="shrink-0 p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Uploader;
